<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: ResearchTreePanel                                     |
| Section: components                                              |
| Purpose: Displays one branch-scoped research tree.               |
+------------------------------------------------------------------+
-->
<template>
    <div :class="['research-tree-panel', {
        'research-tree-panel--visible': visible,
        'research-tree-panel--inline': inline,
    }]" :aria-hidden="!visible" :inert="!visible">
        <div class="research-tree-panel__overlay" @click="handleClose"></div>

        <button v-if="visible" ref="closeButton" class="research-tree-panel__close-external"
            type="button" @click="handleClose" :aria-label="$t('researchPage.panel.close')"
            :title="$t('researchPage.panel.closeHint')">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                    stroke-linejoin="round" />
            </svg>
        </button>

        <section class="research-tree-panel__content" role="dialog" aria-modal="true"
            :aria-labelledby="visible ? 'research-tree-panel-title' : undefined">
            <header class="research-tree-panel__header">
                <div>
                    <p class="research-tree-panel__eyebrow">{{ groupLabel }}</p>
                    <h2 id="research-tree-panel-title" class="research-tree-panel__title">{{ leadName }}</h2>
                    <p class="research-tree-panel__subtitle">
                        {{ getPeopleCount(memberCount) }}
                    </p>
                </div>
            </header>

            <div v-if="tree && tree.members?.length" class="research-tree-panel__body">
                <div v-for="group in memberGroups" :key="group.key"
                    class="research-tree-panel__relationship-group">
                    <h3 class="research-tree-panel__relationship-type">{{ group.label }}</h3>
                    <div class="research-tree-panel__person-cards">
                        <PersonCard v-for="person in group.people" :key="person.personID"
                            :photoSrc="getPersonPhoto(person)" :name="getPersonName(person)"
                            :years="getPersonYears(person)" :gender="getPersonGender(person)"
                            :href="getPersonHref(person)" :personID="person.personID"
                            :firstname="person.firstname" :lastname="person.lastname"
                            :living="person.living" :isSmall="true"
                            :class="{ 'PersonCard_small_guest': isPrivatePerson(person) }" />
                    </div>
                </div>
            </div>

            <div v-else class="research-tree-panel__empty">
                <p>{{ $t('researchPage.panel.empty') }}</p>
            </div>

            <div v-if="leadPerson?.personID" class="research-tree-panel__cta-wrapper">
                <BtnPrimary class="research-tree-panel__cta-button"
                    :label="$t('researchPage.panel.viewLead')" :href="getPersonHref(leadPerson)"
                    :showIcon="false" />
            </div>
        </section>
    </div>
</template>

<script>
import BtnPrimary from '@/atoms/BtnPrimary.vue';
import PersonCard from '@/atoms/PersonCard.vue';
import { formatYears } from '@/utils/dateUtils.js';
import { getDefaultPhoto } from '@/utils/photoUtils';
import { useAuth } from '@/composables/useAuth';
import { isPrivateLivingPerson } from '@/utils/personPrivacy';

export default {
    name: 'ResearchTreePanel',
    components: { BtnPrimary, PersonCard },
    props: {
        visible: { type: Boolean, default: false },
        tree: { type: Object, default: null },
        groupLabel: { type: String, default: '' },
        inline: { type: Boolean, default: false },
    },
    setup() {
        const { authToken } = useAuth();
        return { authToken };
    },
    data() {
        return { previousBodyOverflow: '' };
    },
    computed: {
        leadPerson() {
            return this.tree?.members?.find(person => person.personID === this.tree.leadPersonId) || null;
        },
        leadName() {
            return this.getPersonName(this.leadPerson);
        },
        memberCount() {
            return this.tree?.memberCount || this.tree?.members?.length || 0;
        },
        memberGroups() {
            if (!this.tree?.members?.length) return [];
            const byGeneration = new Map();
            const unplaced = [];

            for (const person of this.tree.members) {
                if (!Number.isInteger(person.generation)) {
                    unplaced.push(person);
                    continue;
                }
                if (!byGeneration.has(person.generation)) byGeneration.set(person.generation, []);
                byGeneration.get(person.generation).push(person);
            }

            const groups = [...byGeneration.entries()]
                .sort((left, right) => left[0] - right[0])
                .map(([generation, people]) => ({
                    key: `generation-${generation}`,
                    label: generation === 0
                        ? this.$t('researchPage.panel.topLevel')
                        : this.$t('researchPage.panel.generation', { generation: generation + 1 }),
                    people,
                }));

            if (unplaced.length) {
                groups.push({
                    key: 'unplaced',
                    label: this.$t('researchPage.panel.otherRelations'),
                    people: unplaced,
                });
            }
            return groups;
        },
    },
    watch: {
        visible(newValue) {
            if (newValue) {
                document.addEventListener('keydown', this.handleEscKey);
                if (!this.inline) {
                    this.previousBodyOverflow = document.body.style.overflow;
                    document.body.style.overflow = 'hidden';
                }
                this.$nextTick(() => this.$refs.closeButton?.focus());
                return;
            }
            this.releasePanelEffects();
        },
    },
    methods: {
        formatYears,
        getPeopleCount(count) {
            const key = count === 1
                ? 'researchPage.panel.peopleCountOne'
                : 'researchPage.panel.peopleCountMany';
            return this.$t(key, { count });
        },
        isPrivatePerson(person) {
            return isPrivateLivingPerson(person, this.authToken);
        },
        getPersonName(person) {
            if (!person) return this.$t('researchPage.panel.unknownPerson');
            return `${person.firstname || ''} ${person.lastname || ''}`.trim()
                || this.$t('researchPage.panel.unknownPerson');
        },
        getPersonPhoto(person) {
            return person?.photoPath || getDefaultPhoto(person?.sex);
        },
        getPersonGender(person) {
            return ['M', 'F'].includes(person?.sex) ? person.sex : 'unknown';
        },
        getPersonYears(person) {
            if (!person) return '';
            return formatYears(
                person.birthdatetr,
                person.altbirthdatetr,
                person.deathdatetr,
                person.burialdatetr,
                person.living,
            );
        },
        getPersonHref(person) {
            return person?.personID ? `/person/${person.personID}` : 'javascript:void(0)';
        },
        handleClose() {
            this.$emit('close');
        },
        handleEscKey(event) {
            if (event.key === 'Escape' && this.visible) this.handleClose();
        },
        releasePanelEffects() {
            document.removeEventListener('keydown', this.handleEscKey);
            if (!this.inline) document.body.style.overflow = this.previousBodyOverflow;
        },
    },
    beforeUnmount() {
        this.releasePanelEffects();
    },
};
</script>

<style scoped>
.research-tree-panel {
    position: fixed;
    inset: 0;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.research-tree-panel--inline { position: absolute; }
.research-tree-panel--visible { pointer-events: all; opacity: 1; }

.research-tree-panel__overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    opacity: 0;
    transition: opacity 0.3s ease;
}

.research-tree-panel--visible .research-tree-panel__overlay { opacity: 1; }

.research-tree-panel__content {
    position: absolute;
    inset: 0 0 0 auto;
    width: 540px;
    max-width: 90vw;
    background: white;
    box-shadow: -4px 0 24px rgba(0, 0, 0, 0.2);
    display: flex;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.research-tree-panel--visible .research-tree-panel__content { transform: translateX(0); }

.research-tree-panel__header {
    padding: 32px 32px 24px;
    border-bottom: 1px solid #e5e5e5;
}

.research-tree-panel__eyebrow {
    margin: 0 0 8px;
    color: var(--brand-color-1);
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}

.research-tree-panel__title {
    margin: 0;
    color: var(--brand-text-dark);
    font-size: 24px;
    font-weight: 600;
    line-height: 1.3;
}

.research-tree-panel__subtitle { margin: 8px 0 0; color: #666; font-size: 14px; }

.research-tree-panel__close-external {
    position: absolute;
    top: 32px;
    right: calc(540px + 12px);
    width: 40px;
    height: 40px;
    padding: 10px;
    z-index: 1001;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--brand-text-dark);
    background: #fff;
    border: none;
    border-radius: var(--border-radius-md);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    transition: var(--transition-ease);
}

.research-tree-panel__close-external:hover,
.research-tree-panel__close-external:focus-visible {
    background: #f0f0f0;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.research-tree-panel__body {
    flex: 1;
    overflow-y: auto;
    padding: 24px 32px 120px;
    scroll-behavior: smooth;
}

.research-tree-panel__empty {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 32px;
    color: #666;
    text-align: center;
}

.research-tree-panel__relationship-group { margin-bottom: 32px; }
.research-tree-panel__relationship-group:last-child { margin-bottom: 0; }

.research-tree-panel__relationship-type {
    margin: 0 0 16px;
    color: var(--brand-text-dark);
    font-size: 18px;
    font-weight: 600;
}

.research-tree-panel__person-cards { display: flex; flex-direction: column; gap: 12px; }

.research-tree-panel__cta-wrapper {
    position: absolute;
    right: 0;
    bottom: 20px;
    left: 0;
    z-index: 2;
    display: flex;
    justify-content: center;
    pointer-events: none;
}

.research-tree-panel__cta-button {
    min-width: 240px;
    justify-content: center;
    pointer-events: all;
}

@media (max-width: 768px) {
    .research-tree-panel__content { width: 100%; max-width: 100vw; }
    .research-tree-panel__header { padding: 24px 56px 20px 20px; }
    .research-tree-panel__title { font-size: 20px; }
    .research-tree-panel__body { padding: 20px 20px 112px; }
    .research-tree-panel__close-external { top: 16px; right: 16px; }
}
</style>
